import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@folio/components';
import { cn } from '@folio/lib';
import type { EmailAccountDto } from 'korint-sdk';
import { ChevronDown, Plus, Settings } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@folio/components/ui/text';
import { EMAIL_ACCOUNT_STATUS_DOT, emailAccountStatusTextClassName } from './emailAccountStatus';

function MailboxOption({
  label,
  subtitle,
  dotClassName,
  unreadCount,
  selected,
  onSelect,
}: {
  label: string;
  subtitle?: ReactNode;
  dotClassName?: string;
  unreadCount: number;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      role="menuitemradio"
      aria-checked={selected}
      onSelect={onSelect}
      className={cn('gap-2.5', selected && 'bg-accent')}
    >
      <span aria-hidden className={cn('size-2 shrink-0 rounded-full', dotClassName)} />
      <div className="min-w-0 flex-1">
        <Text truncate size="sm" weight={selected ? 'semibold' : 'medium'}>
          {label}
        </Text>
        {subtitle}
      </div>
      {unreadCount > 0 && (
        <Badge variant="light" tone="muted" size="sm">
          {unreadCount}
        </Badge>
      )}
    </DropdownMenuItem>
  );
}

export function MailboxFilter({
  accounts,
  showShared,
  unreadCountByAccount,
  totalUnreadCount,
  selectedId,
  onSelect,
  onConnect,
  onManage,
}: {
  accounts: EmailAccountDto[];
  showShared: boolean;
  unreadCountByAccount: ReadonlyMap<string, number>;
  totalUnreadCount: number;
  selectedId: string | null;
  onSelect: (accountId: string | null) => void;
  onConnect: () => void;
  onManage: () => void;
}) {
  const { t } = useTranslation();
  const listed = [
    ...accounts.filter(({ isShared }) => !isShared),
    ...(showShared ? accounts.filter(({ isShared }) => isShared) : []),
  ];
  const selected = listed.find(({ id }) => id === selectedId);
  const hasErroredAccount = listed.some(({ status }) => status === 'error');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="xs" data-testid="general-inbox-mailbox-filter">
          {hasErroredAccount && (
            <span
              role="img"
              aria-label={t('generalInbox.filters.mailboxes.needsReconnection')}
              title={t('generalInbox.filters.mailboxes.needsReconnection')}
              className={cn('mr-1.5 size-1.5 rounded-full', EMAIL_ACCOUNT_STATUS_DOT.error)}
            />
          )}
          {selected?.primaryEmailAddress ?? t('generalInbox.filters.mailboxes.all')}
          <ChevronDown className="ml-1 size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <MailboxOption
          label={t('generalInbox.filters.mailboxes.all')}
          unreadCount={totalUnreadCount}
          selected={!selected}
          onSelect={() => onSelect(null)}
        />
        {listed.map(account => (
          <MailboxOption
            key={account.id}
            label={account.primaryEmailAddress}
            dotClassName={EMAIL_ACCOUNT_STATUS_DOT[account.status]}
            subtitle={
              account.status === 'connected' ? (
                <Text truncate size="xs" tone="muted">
                  {t(account.isShared ? 'generalInbox.mailbox.shared' : 'generalInbox.mailbox.own')}
                </Text>
              ) : (
                <Text
                  truncate
                  size="xs"
                  className={emailAccountStatusTextClassName(account.status)}
                >
                  {t(`connectedEmailAccounts.status.${account.status}`)}
                </Text>
              )
            }
            unreadCount={unreadCountByAccount.get(account.id) ?? 0}
            selected={account.id === selected?.id}
            onSelect={() => onSelect(account.id)}
          />
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onConnect} className="gap-2.5">
          <Plus className="size-3.5" aria-hidden />
          {t('connectedEmailAccounts.connect')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onManage} className="gap-2.5">
          <Settings className="size-3.5" aria-hidden />
          {t('connectedEmailAccounts.manage')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
